import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { format, parseISO } from "date-fns"

import { VerifyOtpRequest } from "commonModule/services/auth/otp"
import { ReSendOtpRequest } from "commonModule/services/auth/resendOtp"
import { ResetPasswordRequest } from "commonModule/services/auth/resetPassword"
import { SaveUserRequest } from "commonModule/services/auth/saveUser"
import { SendOtpRequest } from "commonModule/services/auth/sendOtp"
import { SoftLoginRequestParams } from "commonModule/services/auth/softLogin"
import { SetUpAccountPasswordRequest } from "commonModule/services/thankyou-page/setUpAccountPasswordApi"
import { apiAuthClient } from "services/apiAuthClient"
import { apiClientCommon } from "services/apiClientCommon"
import { apiGAIdClient } from "services/apiGAIdClient"
import { apiUserClient } from "services/apiUserClient"
import { mixpanelV1Client } from "services/mixpanel"
import { mixpanelV2Client } from "services/mixpanelv2"
import { RootState } from "stores/store"
import { canUseDOM } from "utils/dom"
import {
  cleanCookiesStorage,
  getGAId,
  removeReCaptchaToken,
  removeSoftLoginAuthToken,
  removeSoftLoginData,
  removeTemporaryAuthToken,
  removeUserCookie,
  setAuthToken,
  setSoftLoginAuthToken,
  setSoftLoginDataInLS,
  setTemporaryAuthToken,
  setUserCookie,
} from "utils/local-storage"
import { markStates } from "utils/misc"

import {
  AuthInitialState,
  SoftLoginModalType,
  SoftLoginSendMessagePayload,
  SoftLoginStateType,
} from "./types"

export const sendOtpThunk = createAsyncThunk(
  "auth/sendOtp",
  async (request: SendOtpRequest, thunkAPI) => {
    try {
      const response = await apiAuthClient.sendOtp(request)
      // When password was included this is a full login — store the real
      // auth token. Otherwise it's the temp token used during OTP flow.
      if (request.params.password) {
        setAuthToken(response.headers.authorization)
      } else {
        setTemporaryAuthToken(response.headers.authorization)
      }
      return response.data
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      if (error.code === "ECONNABORTED") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return thunkAPI.rejectWithValue(error)
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      setTemporaryAuthToken(error.response.headers.authorization)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return thunkAPI.rejectWithValue(error.response.data)
    }

    // store  temporary token in cookies
  },
)

export const validateGoogleTokenThunk = createAsyncThunk(
  "auth/validateGoogleTokenThunk",
  async (request, thunkAPI) => {
    try {
      const response = await apiAuthClient.validateGoogleToken()
      if (!response || !response.data || !response.data.errorCode) {
        throw Error("Google Validate Token Response Error")
      }
      if (
        response.data.errorCode === "api_email_already_exist" &&
        !response.data?.body?.softLogin
      ) {
        removeTemporaryAuthToken()
        setAuthToken(response.headers.authorization)
      } else if (response.data.errorCode === "api_user_not_found") {
        setTemporaryAuthToken(response.headers.authorization)
      } else if (response.data.errorCode === "api_user_has_been_blocked") {
        return thunkAPI.rejectWithValue(response.data)
      } else if (response.data.errorCode === "token_invalid") {
        throw Error(response.data.errorCode)
      }
      return response.data
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      if (error.code === "ECONNABORTED") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return thunkAPI.rejectWithValue(error)
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return thunkAPI.rejectWithValue(error?.response?.data || "")
    }
  },
)

export const validateAppleTokenThunk = createAsyncThunk(
  "auth/validateAppleTokenThunk",
  async (request, thunkAPI) => {
    try {
      const response = await apiAuthClient.validateAppleToken()
      if (!response || !response.data || !response.data.errorCode) {
        throw Error("Apple Validate Token Response Error")
      }
      if (
        response.data.errorCode === "api_email_already_exist" &&
        !response.data?.body?.softLogin
      ) {
        removeTemporaryAuthToken()
        setAuthToken(response.headers.authorization)
      } else if (response.data.errorCode === "api_user_not_found") {
        setTemporaryAuthToken(response.headers.authorization)
      } else if (response.data.errorCode === "api_user_has_been_blocked") {
        return thunkAPI.rejectWithValue(response.data)
      } else if (response.data.errorCode === "token_invalid") {
        throw Error(response.data.errorCode)
      }
      return response.data
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      if (error.code === "ECONNABORTED") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return thunkAPI.rejectWithValue(error)
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return thunkAPI.rejectWithValue(error.response.data)
    }
  },
)

export const resendOtpThunk = createAsyncThunk(
  "auth/resendOtp",
  async (request: ReSendOtpRequest, thunkAPI) => {
    try {
      const response = await apiAuthClient.resendOtp(request)
      return response.data
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      if (error.code === "ECONNABORTED") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return thunkAPI.rejectWithValue(error)
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return thunkAPI.rejectWithValue(error.response.data)
    }

    // store  temporary token in cookies
  },
)

export const verifyOtpThunk = createAsyncThunk(
  "auth/verifyOtp",
  async (request: VerifyOtpRequest) => {
    const response = await apiAuthClient.verifyOtp(request)
    if (response.headers.authorization) {
      // store token in cookies
      setAuthToken(response.headers.authorization)
    }

    return {
      ...response.data,
      redirectPath: response.headers["x-host-redirection"] || "",
    }
  },
)

export const fetchUserDetails = createAsyncThunk(
  "auth/fetchUserDetails",
  async () => {
    const response = await apiUserClient.getUser()
    return response.data
  },
)

export const saveUserThunk = createAsyncThunk(
  "auth/saveUserThunk",
  async (request: SaveUserRequest) => {
    const response = await apiUserClient.saveUser(request)
    // store token in cookies
    setTemporaryAuthToken(response.headers.authorization)
    return response.data
  },
)

export const saveUserWithEmail = createAsyncThunk(
  "auth/saveUserWithEmail",
  async (request: SaveUserRequest) => {
    const response = await apiUserClient.signUpUserWithEmail(request)
    // store token in cookies
    setAuthToken(response.headers.authorization)

    return response.data
  },
)

export const setUpAccountPassword = createAsyncThunk(
  "auth/setUpAccountPassword",
  async (request: SetUpAccountPasswordRequest) => {
    const response = await apiClientCommon.setUpAccountPassword(request)
    // store token in cookies
    setAuthToken(response.headers.authorization)

    return response.data
  },
)

export const saveGuestUserWithEmail = createAsyncThunk(
  "auth/saveGuestUserWithEmail",
  async (request: SaveUserRequest) => {
    const response = await apiUserClient.signUpGuestUserWithEmail(request)
    // ✅ Return authToken in payload so fulfilled handler can decide
    //    whether to store as full auth token or soft-login token
    return {
      ...response.data,
      authToken: response.headers.authorization,
    }
  },
)

export const verifyPassword = createAsyncThunk(
  "auth/verifyPassword",
  async (request: VerifyOtpRequest) => {
    const response = await apiAuthClient.verifyPassword(request)
    // store token in cookies
    setAuthToken(response.headers.authorization)

    return {
      ...response.data,
      redirectPath: response.headers["x-host-redirection"] || "",
    }
  },
)

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (request: ResetPasswordRequest) => {
    const response = await apiAuthClient.resetPassword(request)

    return response.data
  },
)

export const saveGAId = createAsyncThunk("auth/saveGAId", () => {
  const GAId = getGAId()
  void apiGAIdClient.saveGAIdApi({
    params: {
      id: GAId,
    },
  })
})

export const softLogin = createAsyncThunk(
  "auth/softLogin",
  async (request, thunkAPI) => {
    const authState = (thunkAPI.getState() as RootState).authReducer
    const response = await apiAuthClient.softLogin({
      params: authState.softLogin.softLoginPayload,
    })
    // handle suspended user
    if (response.data.errorCode === "api_user_has_been_blocked") {
      return thunkAPI.rejectWithValue(response.data)
    }
    // store token in cookies
    if (response.data.softLogin) {
      setSoftLoginAuthToken(response.headers.authorization)
      removeTemporaryAuthToken()
    } else {
      setTemporaryAuthToken(response.headers.authorization)
    }

    return response.data
  },
)

const initialState: AuthInitialState = {
  loading: false,
  emailId: "",
  dialCode: "",
  mobileNumber: "",
  countryCode: "",
  userExists: false,
  isLoggedIn: true,
  isFirstLogin: false,
  error: null,
  errorCode: null,
  userBasicDetails: undefined,
  user: undefined,
  isTermAccepted: false,
  ui: {
    isVerifyEmailModalOpen: false,
    isLoginFlow: true,
    isSigningUpWithEmail: false,
    isEmailSent: false,
    modalType: null,
    newLoginSignUpType: null,
    isSuspendedModalOpen: false,
    isLoginInitiatedFromPriceDrop: false,
    actionToPerformAfterLoginOrSignup: null,
  },

  isArriveFromHomePage: false,
  userInfoFromGoogle: null,
  userInfoFromApple: null,

  softLogin: {
    isUserSoftLoggedIn: false,
    softLoginType: null,
    isSoftSignup: false,
    softLoginPayload: {
      emailId: "",
    },
    softSignupType: null,
    softSignupTitle: "",
    softSignupDescription: "",
    softSignupSubTitle: "",
    softSignupCheckboxText: "",
    softLoginSendMessagePayload: null,
    actionToPerformAfterSoftLogin: null,
    setPDAAPIsPayloadData: null,
  },
}

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    markAuthStore: (
      state,
      action: PayloadAction<Partial<AuthInitialState>>,
    ) => {
      markStates(action.payload, state)
    },

    logOut: (state) => {
      markStates(initialState, state)
      state.isLoggedIn = false
      cleanCookiesStorage()

      mixpanelV1Client.LOGOUT({ logoutKey: "Logout" })
      mixpanelV1Client.WEB_SESSION_END()
      mixpanelV1Client.RESET_USER()

      mixpanelV2Client.RESET_USER()
      if (canUseDOM() && window.location.pathname.startsWith("/payment")) {
        const path = localStorage.getItem("detailPagePath") || ""
        window.location.href = path ? path : "/"
      } else if (canUseDOM()) {
        window.location.href = "/"
      }
    },
    markSoftLoginState: (state, action: PayloadAction<SoftLoginStateType>) => {
      // state.softLogin = action.payload
      markStates(action.payload, state.softLogin)
    },
    markSoftLoginPayload: (
      state,
      action: PayloadAction<SoftLoginRequestParams>,
    ) => {
      // state.softLogin = action.payload
      markStates(action.payload, state.softLogin.softLoginPayload)
    },
    clearSoftLoginState: (state) => {
      state.softLogin = {
        isUserSoftLoggedIn: false,
        softLoginType: null,
        isSoftSignup: false,
        softLoginPayload: {
          emailId: "",
        },
        softSignupType: null,
        softLoginSendMessagePayload: null,
      }
    },
    setSoftLoginSendMessagePayload: (
      state,
      action: PayloadAction<SoftLoginSendMessagePayload>,
    ) => {
      state.softLogin.softLoginSendMessagePayload = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendOtpThunk.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(sendOtpThunk.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.errorCode = null
      state.ui.isLoginFlow = true
      state.isFirstLogin = action.payload.isFirstLogin

      // One-step login: when a password was sent, the backend has authenticated
      // the user and returned the real auth token. Mirror verifyPassword.fulfilled
      // so the app reflects the logged-in state immediately.
      if (action.meta.arg.params.password) {
        removeTemporaryAuthToken()
        removeSoftLoginAuthToken()
        removeSoftLoginData()
        state.isLoggedIn = true
        return
      }

      if (!window.location.pathname.startsWith("/payment")) {
        state.ui.modalType = "OTP_OR_PASSWORD_FROM"
      } else {
        state.ui.newLoginSignUpType = "OTP_OR_PASSWORD_FROM"
      }
    })
    builder.addCase(sendOtpThunk.rejected, (state, action) => {
      const { payload }: { payload: any } = action
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.errorCode = payload?.errorCode
      // handle suspended user
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (payload.errorCode === "api_user_has_been_blocked") {
        state.ui.isSuspendedModalOpen = true
        state.ui.modalType = null
        state.loading = false
        state.isLoggedIn = false
        if (window.location.pathname.startsWith("/payment")) {
          state.ui.isLoginFlow = true
        } else {
          state.ui.isLoginFlow = false
        }
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (payload.errorCode == "api_user_not_found") {
        state.ui.isLoginFlow = false
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        state.error = payload.message as string
      }
      state.loading = false
      state.isLoggedIn = false
    })

    builder.addCase(validateGoogleTokenThunk.pending, (state) => {
      state.loading = true
      state.error = null
      state.errorCode = null
    })
    builder.addCase(validateGoogleTokenThunk.fulfilled, (state, action) => {
      state.loading = false
      if (
        action.payload.errorCode === "api_email_already_exist" &&
        !action.payload?.body?.softLogin
      ) {
        state.isLoggedIn = true
        if (!window.location.pathname.startsWith("/payment")) {
          state.ui.modalType = null
        } else {
          state.ui.newLoginSignUpType = null
        }
        state.softLogin.softLoginType = null
      } else if (
        action.payload.errorCode === "api_user_not_found" ||
        action.payload.body.softLogin
      ) {
        if (state.softLogin.softLoginType) {
          state.softLogin.softLoginPayload.emailId =
            action.payload.body.emailAddress
          state.softLogin.softLoginPayload.softLoginFrom = "GOOGLE"
        } else {
          state.ui.isSigningUpWithEmail = true
          state.ui.isLoginFlow = false

          if (!window.location.pathname.startsWith("/payment")) {
            state.ui.modalType = "SIGNUP_DETAILS_FORM"
          } else {
            state.ui.newLoginSignUpType = "SIGNUP_DETAILS_FORM"
          }
          state.userInfoFromGoogle = action.payload.body
        }
      }
    })
    builder.addCase(validateGoogleTokenThunk.rejected, (state, action) => {
      state.loading = false
      const { payload }: { payload: any } = action
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.errorCode = payload?.errorCode
      // handle suspended user
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (payload?.errorCode === "api_user_has_been_blocked") {
        state.ui.isSuspendedModalOpen = true
        state.ui.modalType = null
        state.isLoggedIn = false
        if (window.location.pathname.startsWith("/payment")) {
          state.ui.isLoginFlow = true
        } else {
          state.ui.isLoginFlow = false
        }
        return
      }
    })

    builder.addCase(validateAppleTokenThunk.pending, (state) => {
      state.loading = true
      state.error = null
      state.errorCode = null
    })
    builder.addCase(validateAppleTokenThunk.fulfilled, (state, action) => {
      state.loading = false
      if (
        action.payload.errorCode === "api_email_already_exist" &&
        !action.payload?.body?.softLogin
      ) {
        state.isLoggedIn = true
        if (!window.location.pathname.startsWith("/payment")) {
          state.ui.modalType = null
        } else {
          state.ui.newLoginSignUpType = null
        }
        state.softLogin.softLoginType = null
      } else if (
        action.payload.errorCode === "api_user_not_found" ||
        action.payload?.body?.softLogin
      ) {
        if (state.softLogin.softLoginType) {
          state.softLogin.softLoginPayload.emailId =
            action.payload.body.emailAddress
          state.softLogin.softLoginPayload.softLoginFrom = "APPLE"
        } else {
          state.ui.isSigningUpWithEmail = true
          state.ui.isLoginFlow = false

          if (!window.location.pathname.startsWith("/payment")) {
            state.ui.modalType = "SIGNUP_DETAILS_FORM"
          } else {
            state.ui.newLoginSignUpType = "SIGNUP_DETAILS_FORM"
          }
          state.userInfoFromApple = action.payload.body
        }
      }
    })
    builder.addCase(validateAppleTokenThunk.rejected, (state, action) => {
      state.loading = false
      const { payload }: { payload: any } = action
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.errorCode = payload?.errorCode
      // handle suspended user
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (payload?.errorCode === "api_user_has_been_blocked") {
        state.ui.isSuspendedModalOpen = true
        state.ui.modalType = null
        state.isLoggedIn = false
        if (window.location.pathname.startsWith("/payment")) {
          state.ui.isLoginFlow = true
        } else {
          state.ui.isLoginFlow = false
        }
        return
      }
    })

    builder.addCase(resendOtpThunk.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(resendOtpThunk.fulfilled, (state) => {
      state.loading = false
      state.error = null

      if (!window.location.pathname.startsWith("/payment")) {
        state.ui.modalType = "OTP_OR_PASSWORD_FROM"
      } else {
        state.ui.newLoginSignUpType = "OTP_OR_PASSWORD_FROM"
      }
    })
    builder.addCase(resendOtpThunk.rejected, (state, action) => {
      const payload = action.payload as { errorCode?: string; message?: string }
      if (payload?.errorCode === "api_user_not_found") {
        state.ui.isLoginFlow = false
        if (!window.location.pathname.startsWith("/payment")) {
          state.ui.modalType = "SIGNUP_DETAILS_FORM"
        } else {
          state.ui.newLoginSignUpType = "SIGNUP_DETAILS_FORM"
        }
      } else {
        state.error = payload?.message || ""
      }
      state.loading = false
      state.isLoggedIn = false
    })

    builder.addCase(verifyOtpThunk.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(verifyOtpThunk.fulfilled, (state, action) => {
      if (
        !action.payload?.isEmailVerify &&
        !window?.location.pathname.startsWith("/payment")
      ) {
        state.ui.isVerifyEmailModalOpen = true
      }
      const user = {
        userId: action.payload?.userId,
        firstName: action.payload?.firstName,
      }
      // remove temp token from cookies
      removeTemporaryAuthToken()
      // remove soft login auth token
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      // Store user inside cookies
      setUserCookie(user)
      // Store user session isValid and user detail to redux
      state.loading = false
      state.userBasicDetails = user
      state.isLoggedIn = true
    })
    builder.addCase(verifyOtpThunk.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
      state.errorCode = action.error.code || null
      removeUserCookie()
    })

    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      state.user = action.payload
      const user = {
        userId: action.payload.userId,
        firstName: action.payload.firstName,
      }
      // if terms are not accepted open signup modal.
      // signup modal will have prefield values from user object.
      // this case will mostly happen when user is created in Host or RBO portal.
      if (!action.payload.isTrmsncndAccepted) {
        state.ui.isSigningUpWithEmail = false
        state.ui.modalType = "SIGNUP_DETAILS_FORM"
      }
      // remove temp token from cookies
      removeTemporaryAuthToken()
      removeReCaptchaToken()
      // remove soft login auth token
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      // Store user inside cookies
      setUserCookie(user)
      // Store user session isValid and user detail to redux
      state.userBasicDetails = user

      if (action.payload) {
        const signUpDate = action.payload.createdDt
          ? new Date(action.payload.createdDt)?.toISOString()
          : ""

        mixpanelV2Client.IDENTIFY(action.payload.userId)
        mixpanelV2Client.SET_PEOPLE({
          $name: `${action.payload.firstName} ${action.payload.lastName || ""}`,
          $first_name: action.payload.firstName,
          $last_name: action.payload.lastName,
          $phone: action.payload.dialerCode
            ? `${action.payload.dialerCode}${action.payload.mobileno1 || ""}`
            : action.payload.mobileno1,
          $email: action.payload.emailId,
          $DOB: action.payload.dobDt
            ? format(parseISO(action.payload.dobDt), "yyyy-MM-dd, h:mm:ss aaa")
            : "NA",
          ...(action.payload.createdDt ? { $sign_up_date: signUpDate } : {}),
          $Verified_email: action.payload.emailVerified,
        })

        mixpanelV1Client.IDENTIFY(action.payload.userId)
        mixpanelV1Client.SET_PEOPLE({
          $name: `${action.payload.firstName} ${action.payload.lastName || ""}`,
          $first_name: action.payload.firstName,
          $last_name: action.payload.lastName,
          $phone: action.payload.dialerCode
            ? `${action.payload.dialerCode}${action.payload.mobileno1 || ""}`
            : action.payload.mobileno1,
          $email: action.payload.emailId,
          $DOB: action.payload.dobDt
            ? format(parseISO(action.payload.dobDt), "yyyy-MM-dd, h:mm:ss aaa")
            : "NA",

          ...(action.payload.createdDt ? { $sign_up_date: signUpDate } : {}),
          $Verified_email: action.payload.emailVerified,
        })
      }
    })
    builder.addCase(fetchUserDetails.rejected, (state, action) => {
      if (action.error.code == "401") {
        state.userBasicDetails = undefined
        cleanCookiesStorage()
      }
    })

    builder.addCase(saveUserThunk.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })

    builder.addCase(saveUserThunk.fulfilled, (state) => {
      state.loading = false
      state.error = null
      state.ui.isEmailSent = true
      if (!window.location.pathname.startsWith("/payment")) {
        state.ui.modalType = "OTP_OR_PASSWORD_FROM"
      } else {
        state.ui.newLoginSignUpType = "OTP_OR_PASSWORD_FROM"
      }
    })

    builder.addCase(saveUserThunk.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
    })
    builder.addCase(saveUserWithEmail.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })

    builder.addCase(saveGuestUserWithEmail.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(saveUserWithEmail.fulfilled, (state, action) => {
      state.loading = false
      if (
        action.payload &&
        !action.payload.isEmailVerify &&
        !window?.location.pathname.startsWith("/payment") &&
        // we are displaying new toast for soft login so we do not need to display this verify email modal
        !state.softLogin.softLoginPayload.emailId
      ) {
        state.ui.isVerifyEmailModalOpen = true
        state.ui.isEmailSent = true
      }
      const user = {
        userId: action.payload.userId,
        firstName: action.payload.firstName,
      }
      // remove temp token from cookies
      removeTemporaryAuthToken()
      // remove soft login token
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      // Store user inside cookies
      setUserCookie(user)
      // Store user session isValid and user detail to redux
      state.userBasicDetails = user
      state.isLoggedIn = true
      state.error = null
      if (!window.location.pathname.startsWith("/payment")) {
        state.ui.modalType = null
      } else {
        state.ui.newLoginSignUpType = null
      }
    })

    builder.addCase(saveGuestUserWithEmail.fulfilled, (state, action) => {
      state.loading = false

      const isPaymentPage = window?.location.pathname.startsWith("/payment")
      const isBookingSoftLogin = action?.meta?.arg?.params?.user?.password

      if (action.payload && !action.payload.isEmailVerify && !isPaymentPage) {
        state.ui.isVerifyEmailModalOpen = true
        state.ui.isEmailSent = true
      }

      const user = {
        userId: action.payload.userId,
        firstName: action.payload.firstName,
      }

      // ✅ COLLAPSIBLE FLOW — Guest checkout on /payment (Variant B):
      //    Store JWT as soft-login token. User stays as guest (isLoggedIn = false).
      //    Booking proceeds with userId from this response.
      if (isPaymentPage && !isBookingSoftLogin) {
        // Store as soft-login token (not full auth) — mirrors softLogin thunk
        setSoftLoginAuthToken(action.payload.authToken)
        // Do NOT call setAuthToken — this is not a full login

        setSoftLoginDataInLS({
          emailId: state.softLogin.softLoginPayload.emailId,
          softLoginType: SoftLoginModalType.BOOKING_SOFT_LOGIN,
          userId: action.payload.userId,
          isSoftLogingFromFavOrPDA: false,
          isSoftLogingFromMessageHost: false,
        })

        state.softLogin.isUserSoftLoggedIn = true
        // state.userBasicDetails = user
        // ✅ isLoggedIn stays false — guest checkout only
        state.error = null
        state.ui.newLoginSignUpType = null
        return
      }
      // ── EXISTING flow (non-payment page OR old Variant A flow) ─────────────
      // Store as full auth token (original behavior)
      setAuthToken(action.payload.authToken)
      removeTemporaryAuthToken()
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      setUserCookie(user)
      state.userBasicDetails = user
      // state.isLoggedIn = true  ← intentionally kept commented (original)
      state.error = null
      if (!isPaymentPage) {
        state.ui.modalType = null
      } else {
        state.ui.newLoginSignUpType = null
      }
    })
    builder.addCase(saveUserWithEmail.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
    })

    builder.addCase(saveGuestUserWithEmail.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
    })
    builder.addCase(verifyPassword.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(verifyPassword.fulfilled, (state, action) => {
      if (
        !action.payload.isEmailVerify &&
        !window?.location.pathname.startsWith("/payment")
      ) {
        state.ui.isVerifyEmailModalOpen = true
      }
      const user = {
        userId: action.payload.userId,
        firstName: action.payload.firstName,
      }
      // remove temp token from cookies
      removeTemporaryAuthToken()
      // remove soft login auth token
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      // Store user inside cookies
      setUserCookie(user)
      // Store user session isValid and user detail to redux
      state.loading = false
      state.userBasicDetails = user
      state.isLoggedIn = true
    })
    builder.addCase(verifyPassword.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
      state.errorCode = action.error.code || null
    })
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false
      state.error = null
      if (!window.location.pathname.startsWith("/payment")) {
        state.ui.modalType = null
      } else {
        state.ui.newLoginSignUpType = null
      }
    })
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
    })
    builder.addCase(softLogin.pending, () => {})
    builder.addCase(softLogin.fulfilled, (state, action) => {
      if (action.payload.softLogin) {
        mixpanelV2Client.IDENTIFY(action.payload.userId)
        mixpanelV2Client.COMPLETE_SOFT_LOGIN({
          emailId: state.softLogin.softLoginPayload.emailId,
          pageOrigin:
            state.softLogin.softLoginType === SoftLoginModalType.PDA_SOFT_LOGIN
              ? "price drop alert"
              : state.softLogin.softLoginType ===
                SoftLoginModalType.FAV_SOFT_LOGIN
              ? "property favorite"
              : "ask question to host",
          signupType:
            state.softLogin.softLoginPayload.softLoginFrom === "GOOGLE"
              ? "google"
              : state.softLogin.softLoginPayload.softLoginFrom === "APPLE"
              ? "apple"
              : "email",
        })
        setSoftLoginDataInLS({
          emailId: state.softLogin.softLoginPayload.emailId,
          softLoginType: state.softLogin.softLoginType as SoftLoginModalType,
          userId: action.payload.userId,
          isSoftLogingFromFavOrPDA:
            state.softLogin.softLoginType ===
              SoftLoginModalType.FAV_SOFT_LOGIN ||
            state.softLogin.softLoginType === SoftLoginModalType.PDA_SOFT_LOGIN,
          isSoftLogingFromMessageHost:
            state.softLogin.softLoginType ===
            SoftLoginModalType.MSG_HOST_SOFT_LOGIN,
        })
        state.softLogin.isUserSoftLoggedIn = true
        state.softLogin.softLoginType = null
        state.softLogin.isSoftSignup = true
        state.ui.modalType = "SIGNUP_DETAILS_FORM"
        state.ui.isSigningUpWithEmail = true
        state.ui.actionToPerformAfterLoginOrSignup = null
      } else {
        state.softLogin.softLoginType = null
        state.ui.isLoginFlow = true
        state.ui.isSigningUpWithEmail = true
        state.ui.modalType = "OTP_OR_PASSWORD_FROM"
      }
    })
    builder.addCase(softLogin.rejected, (state, action) => {
      const { error }: { error: any } = action
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.errorCode = error?.code
      // handle suspended user
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (error?.code === "api_user_has_been_blocked") {
        state.ui.isSuspendedModalOpen = true
        state.softLogin = {
          isUserSoftLoggedIn: false,
          softLoginType: null,
          isSoftSignup: false,
          softLoginPayload: {
            emailId: "",
            softLoginFrom: null,
          },
          softSignupType: null,
          softLoginSendMessagePayload: null,
          actionToPerformAfterSoftLogin: null,
        }
        state.isLoggedIn = false
        state.ui.isLoginFlow = false
        return
      }
    })

    builder.addCase(setUpAccountPassword.pending, (state) => {
      state.loading = true
      state.isLoggedIn = false
      state.error = null
    })
    builder.addCase(setUpAccountPassword.fulfilled, (state, action) => {
      state.loading = false
      // remove temp token from cookies
      removeTemporaryAuthToken()
      // remove soft login token
      removeSoftLoginAuthToken()
      removeSoftLoginData()
      state.isLoggedIn = true
      state.error = null
    })
    builder.addCase(setUpAccountPassword.rejected, (state, action) => {
      state.loading = false
      state.isLoggedIn = false
      state.error = action.error.message as string
    })
  },
})

export const {
  logOut,
  markAuthStore,
  markSoftLoginState,
  markSoftLoginPayload,
  clearSoftLoginState,
  setSoftLoginSendMessagePayload,
} = authSlice.actions

export const selectIsUserLoggedIn = (state: RootState) =>
  state.authReducer.isLoggedIn
export const selectIsUserSoftLoggedIn = (state: RootState) =>
  state.authReducer.softLogin.isUserSoftLoggedIn

export const selectDialCode = (state: RootState) => state.authReducer.dialCode
export const selectUser = (state: RootState) => state.authReducer.user
export const selectIsTrmsncndAcceptedByUser = (state: RootState) =>
  state.authReducer.user?.isTrmsncndAccepted
export const selectUserBasicDetails = (state: RootState) =>
  state.authReducer.userBasicDetails
export const selectMobileNumber = (state: RootState) =>
  state.authReducer.mobileNumber
export const selectCountryCode = (state: RootState) =>
  state.authReducer.countryCode
export const selectEmailId = (state: RootState) => state.authReducer.emailId
export const selectIsFirstLogin = (state: RootState) =>
  state.authReducer.isFirstLogin
export const selectAuthLoading = (state: RootState) => state.authReducer.loading
export const selectAuthError = (state: RootState) => state.authReducer.error
export const selectAuthErrorCode = (state: RootState) =>
  state.authReducer.errorCode
export const selectAuthUI = (state: RootState) => state.authReducer.ui
export const selectsoftLoginUI = (state: RootState) =>
  state.authReducer.softLogin

export const selectUserInfoFromGoogle = (state: RootState) =>
  state.authReducer.userInfoFromGoogle
export const selectUserInfoFromApple = (state: RootState) =>
  state.authReducer.userInfoFromApple

export const selectIsArriveFromHomePage = (state: RootState) =>
  state.authReducer.isArriveFromHomePage
export const selectAgentRole = (state: RootState) =>
  state.authReducer.user?.agentRole ?? null
export default authSlice.reducer
